import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import {
  ExternalLink,
  IconEdit,
  IconSuccess,
  PrimaryButton,
  InlineTextButton,
} from '../../components';
import css from './StripeConnectAccountStatusBox.module.css';

// Where a seller whose payouts are blocked can reach a human. A rejection is permanent
// and Stripe's onboarding flow can't clear it, so this is their only route back to us.
const SUPPORT_EMAIL = 'Support@shimmybooks.co.uk';

const STATUS_VERIFICATION_NEEDED = 'verificationNeeded';
const STATUS_VERIFICATION_SUCCESS = 'verificationSuccess';
const STATUS_VERIFICATION_ERROR = 'verificationError';
const STATUS_RESTRICTED = 'restricted';

// Stripe reason codes we have written copy for. Anything else falls back to a generic
// explanation, so an unrecognised code still tells the seller their payouts are blocked.
const KNOWN_DISABLED_REASONS = [
  'rejected.fraud',
  'rejected.listed',
  'rejected.terms_of_service',
  'rejected.incomplete_verification',
  'rejected.other',
  'listed',
  'under_review',
  'platform_paused',
  'other',
];

const reasonMessageId = disabledReason =>
  KNOWN_DISABLED_REASONS.includes(disabledReason)
    ? `StripeConnectAccountStatusBox.reason.${disabledReason}`
    : 'StripeConnectAccountStatusBox.reason.fallback';

const StripeConnectAccountStatusBox = props => {
  const { type, onGetStripeConnectAccountLink, inProgress, disabled, buttonTitle, restriction } =
    props;

  if (type === STATUS_RESTRICTED) {
    const { disabledReason, isPermanent, errors = [] } = restriction || {};

    return (
      <div className={classNames(css.verificiationBox, css.verficiationErrorBox)}>
        <div className={css.verificiationBoxTextWrapper}>
          <div className={css.verificationBoxTitle}>
            <FormattedMessage
              id={
                isPermanent
                  ? 'StripeConnectAccountStatusBox.rejectedTitle'
                  : 'StripeConnectAccountStatusBox.restrictedTitle'
              }
            />
          </div>
          <div className={css.verificationBoxText}>
            <FormattedMessage id={reasonMessageId(disabledReason)} />
          </div>

          {errors.length > 0 ? (
            <div className={css.restrictionDetails}>
              <div className={css.restrictionDetailsLabel}>
                <FormattedMessage id="StripeConnectAccountStatusBox.reasonDetailsLabel" />
              </div>
              <ul className={css.restrictionReasonList}>
                {/* Stripe-authored text. Rendered as children so React escapes it — never
                    as markup, and never passed through a rich-text helper. */}
                {errors.map((error, i) => (
                  <li key={error.code || i}>{error.reason}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className={css.verificationBoxText}>
            <FormattedMessage
              id="StripeConnectAccountStatusBox.contactSupport"
              values={{
                supportEmail: (
                  <ExternalLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</ExternalLink>
                ),
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (type === STATUS_VERIFICATION_NEEDED) {
    return (
      <div className={classNames(css.verificiationBox, css.verficiationNeededBox)}>
        <div className={css.verificiationBoxTextWrapper}>
          <div className={css.verificationBoxTitle}>
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededTitle" />
          </div>
          <div className={css.verificationBoxText}>
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededText" />
          </div>
        </div>

        <PrimaryButton
          className={css.getVerifiedButton}
          spinnerClassName={css.spinner}
          type="button"
          inProgress={inProgress}
          disabled={disabled}
          onClick={onGetStripeConnectAccountLink}
          title={buttonTitle}
        >
          <FormattedMessage id="StripeConnectAccountStatusBox.getVerifiedButton" />
        </PrimaryButton>
      </div>
    );
  } else if (type === STATUS_VERIFICATION_SUCCESS) {
    return (
      <div className={classNames(css.verificiationBox, css.verficiationSuccessBox)}>
        <div
          className={classNames(
            css.verificiationBoxTextWrapper,
            css.verificationBoxSuccessTextWrapper
          )}
        >
          <div className={css.verificationBoxTitle}>
            <IconSuccess className={css.icon} />
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationSuccessTitle" />
          </div>
        </div>

        <InlineTextButton
          className={css.editVerificationButton}
          spinnerClassName={css.spinner}
          type="button"
          inProgress={inProgress}
          disabled={disabled}
          onClick={onGetStripeConnectAccountLink}
          title={buttonTitle}
        >
          <IconEdit className={css.icon} pencilClassName={css.iconEditPencil} />
          <FormattedMessage id="StripeConnectAccountStatusBox.editAccountButton" />
        </InlineTextButton>
      </div>
    );
  } else if (type === STATUS_VERIFICATION_ERROR) {
    return (
      <div className={classNames(css.verificiationBox, css.verficiationErrorBox)}>
        <div className={css.verificiationBoxTextWrapper}>
          <div className={css.verificationBoxTitle}>
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationFailedTitle" />
          </div>
          <div className={css.verificationBoxText}>
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationFailedText" />
          </div>
        </div>

        <PrimaryButton
          className={css.getVerifiedButton}
          spinnerClassName={css.spinner}
          type="button"
          inProgress={inProgress}
          disabled={disabled}
          title={buttonTitle}
          onClick={onGetStripeConnectAccountLink}
        >
          <FormattedMessage id="StripeConnectAccountStatusBox.getVerifiedButton" />
        </PrimaryButton>
      </div>
    );
  } else {
    throw new Error(`Unknown type ${type} for StripeConnectAccountStatusBox`);
  }
};

export default StripeConnectAccountStatusBox;
